/**
 * Categories — playable surface.
 *
 * Mounts the engine, draws the 6x5 tile grid, and orchestrates the takeover
 * sequence for clue / daily-double / answer / reveal / round-end overlays.
 *
 * The surrounding chrome (back button, score, mode label) lives on a warm
 * editorial frame; the blue board panel sits centered in that frame, brass-
 * outlined. The clue takeover fades the board behind a deep-blue full-screen
 * panel with white-on-blue, all-caps clue text.
 */
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { triggerHaptic, useCountUp } from '@/juice';
import { usePreferences } from '@/preferences';

import {
  formatDollars,
  useBoardEngine,
  type BoardEngineState,
  type BoardTileState,
} from '@/games/categories/engine';
import { CATEGORY_LABELS } from '@/games/categories/boards';
import type { DifficultyEngine, GameSession } from '@rendever/games-sdk';

import { Clue } from './Clue';
import { DailyDouble } from './DailyDouble';
import { Answer } from './Answer';
import { RoundEnd } from './RoundEnd';
import { Celebration } from './Celebration';
import { sound } from './sound';
import { pageSpring } from './motion';

import './Categories.css';

export interface CategoriesProps {
  session: GameSession;
  difficulty: DifficultyEngine;
  mode?: 'daily' | 'practice';
}

export function Categories({
  session,
  difficulty,
  mode = 'daily',
}: CategoriesProps): React.JSX.Element {
  const reduced = useReducedMotion();
  const { theme } = usePreferences();
  const [playMode, setPlayMode] = useState<'daily' | 'practice'>(mode);

  const { state, actions } = useBoardEngine({
    session,
    difficulty,
    mode: playMode,
  });

  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    sound.unlock();
    actions.start();
    // Demo helper: ?demo=midplay reveals a handful of tiles so screenshots
    // catch the board mid-game.
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('demo') === 'midplay') {
        window.setTimeout(() => {
          // Reveal a scattering of tiles by walking the engine like a normal
          // player would — only via actions, so the engine stays consistent.
          // For screenshots we use the dev hook directly.
          const idxs = [0, 7, 14, 22, 28, 5, 11, 17];
          idxs.forEach((i) => {
            const tile = state.tiles[i];
            if (!tile || tile.revealed) return;
            // Synthetic correct answer.
            (
              window as unknown as {
                __boardActions?: {
                  selectTile: (i: number) => void;
                  submitAnswer: (s: string) => void;
                  dismissReveal: () => void;
                };
              }
            ).__boardActions?.selectTile(i);
            (
              window as unknown as {
                __boardActions?: {
                  submitAnswer: (s: string) => void;
                };
              }
            ).__boardActions?.submitAnswer(tile.clue.canonicalAnswer);
            (
              window as unknown as {
                __boardActions?: {
                  dismissReveal: () => void;
                };
              }
            ).__boardActions?.dismissReveal();
          });
        }, 200);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manage think-music: play during clue + answer phases, stop everywhere else.
  useEffect(() => {
    if (state.phase === 'clue' || state.phase === 'answer') {
      sound.startThinkMusic();
    } else {
      sound.stopThinkMusic();
    }
  }, [state.phase]);

  // Stop think-music on unmount.
  useEffect(() => {
    return () => {
      sound.stopThinkMusic();
    };
  }, []);

  const handleTileClick = useCallback(
    (index: number) => {
      const tile = state.tiles[index];
      if (!tile || tile.revealed) return;
      sound.play('tileSelect');
      triggerHaptic('light');
      actions.selectTile(index);
      // Cue the clue-reveal sound on the next frame.
      window.setTimeout(() => {
        if (tile.dailyDouble) {
          sound.play('dailyDouble');
        } else {
          sound.play('clueReveal');
        }
      }, 120);
    },
    [state.tiles, actions],
  );

  const handleSubmitAnswer = useCallback(
    (text: string) => {
      sound.play('submit');
      triggerHaptic('medium');
      actions.submitAnswer(text);
    },
    [actions],
  );

  const handlePass = useCallback(() => {
    sound.play('incorrect');
    triggerHaptic('light');
    actions.passClue();
  }, [actions]);

  const handleDismissReveal = useCallback(() => {
    sound.play('nav');
    actions.dismissReveal();
  }, [actions]);

  const handleBack = useCallback(() => {
    sound.play('nav');
    if (typeof window !== 'undefined') {
      window.location.href = '/categories';
    }
  }, []);

  const handlePlayAgain = useCallback(() => {
    sound.play('nav');
    if (playMode === 'daily') {
      setPlayMode('practice');
      actions.playAgain('practice');
      return;
    }
    actions.playAgain();
  }, [actions, playMode]);

  // Watch for correct/incorrect transitions and fire the matching sound.
  const prevPhase = useRef(state.phase);
  useEffect(() => {
    if (prevPhase.current !== 'answer' && state.phase === 'reveal') {
      // Manual reveal (the engine's submitAnswer fires directly; this catches
      // edge cases like keyboard submit).
      if (state.lastAnswerCorrect === true) {
        sound.play('correct');
      } else if (state.lastAnswerCorrect === false) {
        sound.play('incorrect');
      }
    }
    if (prevPhase.current === 'board' && state.phase === 'roundEnd') {
      sound.play('roundEnd');
    }
    prevPhase.current = state.phase;
  }, [state.phase, state.lastAnswerCorrect]);

  const activeTile = state.activeTileIndex !== null ? state.tiles[state.activeTileIndex] : null;

  return (
    <div className="tb-play" data-theme={theme} data-phase={state.phase}>
      <PageBackdrop />

      <motion.header
        className="tb-play__head"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={pageSpring}
      >
        <button
          type="button"
          className="tb-play__back"
          onClick={handleBack}
          aria-label="Back to Categories hub"
        >
          <span className="tb-play__back-icon" aria-hidden="true">
            ←
          </span>
          <span className="tb-play__back-text">Categories</span>
        </button>

        <div className="tb-play__marquee">
          <span className="tb-play__mode">
            {playMode === 'daily' ? 'Daily board' : 'Practice board'}
          </span>
          <h1 className="tb-play__title">Categories</h1>
          <div className="tb-play__remaining" aria-live="polite">
            <span className="tb-play__remaining-count">{state.tilesRemaining}</span>
            <span className="tb-play__remaining-label">
              {state.tilesRemaining === 1 ? 'clue left' : 'clues left'}
            </span>
          </div>
        </div>

        <ScorePanel score={state.score} delta={state.lastDelta} phase={state.phase} />
      </motion.header>

      <main className="tb-play__main">
        <BoardGrid state={state} onTileClick={handleTileClick} reduced={!!reduced} />
      </main>

      <AnimatePresence mode="wait">
        {state.phase === 'wager' && activeTile && (
          <motion.div
            key="wager"
            className="tb-play__overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.32 }}
          >
            <DailyDouble
              tile={activeTile}
              score={state.score}
              currentWager={state.wager}
              onSetWager={(amount) => {
                sound.play('wagerTick');
                actions.setWager(amount);
              }}
              onConfirm={() => {
                sound.play('clueReveal');
                actions.proceedToAnswer();
              }}
            />
          </motion.div>
        )}

        {(state.phase === 'clue' || state.phase === 'answer') && activeTile && (
          <motion.div
            key="clue"
            className="tb-play__overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.34 }}
          >
            <Clue
              tile={activeTile}
              showAnswerInput={state.phase === 'answer'}
              onProceedToAnswer={() => {
                sound.play('nav');
                actions.proceedToAnswer();
              }}
            />
            {state.phase === 'answer' && (
              <Answer
                tile={activeTile}
                wager={activeTile.dailyDouble ? state.wager : activeTile.value}
                onSubmit={handleSubmitAnswer}
                onPass={handlePass}
              />
            )}
          </motion.div>
        )}

        {state.phase === 'reveal' && activeTile && (
          <motion.div
            key="reveal"
            className="tb-play__overlay tb-play__overlay--reveal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.34 }}
          >
            <Celebration
              tile={activeTile}
              correct={state.lastAnswerCorrect === true}
              delta={state.lastDelta}
              answerText={state.lastAnswerText}
              onContinue={handleDismissReveal}
            />
          </motion.div>
        )}

        {state.phase === 'roundEnd' && (
          <motion.div
            key="round-end"
            className="tb-play__overlay tb-play__overlay--summary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <RoundEnd
              score={state.score}
              tiles={state.tiles}
              mode={playMode}
              onAnother={handlePlayAgain}
              onBack={handleBack}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Board grid
// ---------------------------------------------------------------------------

function BoardGrid({
  state,
  onTileClick,
  reduced,
}: {
  state: BoardEngineState;
  onTileClick: (i: number) => void;
  reduced: boolean;
}): React.JSX.Element {
  return (
    <>
      <div className="tb-board tb-board--desktop" aria-label="Categories board">
        {/* Category headers */}
        <div className="tb-board__categories" role="rowgroup">
          {state.categoryLabels.map((label, col) => (
            <div key={col} className="tb-board__category" role="columnheader">
              <span className="tb-board__category-text">{label.toUpperCase()}</span>
            </div>
          ))}
        </div>

        {/* Tile grid */}
        <div className="tb-board__grid" role="grid">
          {state.tiles.map((tile, i) => (
            <Tile key={tile.id} tile={tile} index={i} onClick={onTileClick} reduced={reduced} />
          ))}
        </div>
      </div>

      {/* On a phone, six narrow columns make both category names and dollar
          values illegible. Keep the game-show board character, but turn each
          category into a full-width shelf with five generous clue buttons. */}
      <div className="tb-board tb-board--mobile" aria-label="Categories board">
        {state.categoryLabels.map((label, col) => (
          <section className="tb-mobile-category" aria-label={label} key={col}>
            <h2 className="tb-mobile-category__title">{label}</h2>
            <div className="tb-mobile-category__values">
              {state.tiles.map((tile, i) =>
                i % state.categoryLabels.length === col ? (
                  <Tile
                    key={tile.id}
                    tile={tile}
                    index={i}
                    onClick={onTileClick}
                    reduced={reduced}
                  />
                ) : null,
              )}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function Tile({
  tile,
  index,
  onClick,
  reduced,
}: {
  tile: BoardTileState;
  index: number;
  onClick: (i: number) => void;
  reduced: boolean;
}): React.JSX.Element {
  // Stagger the entrance by index — a wave across the board.
  const initialDelay = reduced ? 0 : 0.06 + (index % 6) * 0.04 + Math.floor(index / 6) * 0.05;
  const resultKind = tile.correct ? 'solved' : 'no-score';

  return (
    <motion.button
      type="button"
      className="tb-tile"
      data-revealed={tile.revealed ? 'true' : 'false'}
      data-correct={tile.revealed ? (tile.correct ? 'true' : 'false') : undefined}
      data-daily-double={tile.dailyDouble ? 'true' : 'false'}
      disabled={tile.revealed}
      onClick={() => onClick(index)}
      aria-label={
        tile.revealed
          ? `${CATEGORY_LABELS[tile.category]} for $${tile.value}, ${
              tile.correct ? `solved for ${formatDollars(tile.value)}` : 'no score'
            }`
          : `${CATEGORY_LABELS[tile.category]} for $${tile.value}`
      }
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        delay: initialDelay,
        type: 'spring',
        stiffness: 280,
        damping: 22,
      }}
    >
      {tile.revealed ? (
        <motion.span
          className="tb-tile__result"
          data-result={resultKind}
          initial={reduced ? false : { opacity: 0, scale: 0.82, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 340, damping: 22, delay: reduced ? 0 : 0.04 }}
          aria-hidden="true"
        >
          <span className="tb-tile__result-label">{tile.correct ? 'Solved' : 'No score'}</span>
          <span className="tb-tile__result-value">
            {tile.correct ? `+${formatDollars(tile.value)}` : '$0'}
          </span>
        </motion.span>
      ) : (
        <span className="tb-tile__value" data-value-size={tile.value >= 1000 ? 'long' : 'standard'}>
          ${tile.value}
        </span>
      )}
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// Score panel — three "podium" segments, with a count-up on score change
// ---------------------------------------------------------------------------

function ScorePanel({
  score,
  delta,
  phase,
}: {
  score: number;
  delta: number;
  phase: BoardEngineState['phase'];
}): React.JSX.Element {
  // Animate the score up as it changes.
  const displayed = useCountUp({ to: score, durationMs: 900 });
  const showDeltaFlash = phase === 'reveal' && delta > 0;

  return (
    <div className="tb-score" data-hot={showDeltaFlash ? 'true' : 'false'} aria-live="polite">
      <span className="tb-score__label">Score</span>
      <span className="tb-score__value">{formatDollars(displayed)}</span>
      {showDeltaFlash && (
        <motion.span
          key={delta}
          className="tb-score__delta"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          +{formatDollars(delta)}
        </motion.span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Backdrop
// ---------------------------------------------------------------------------

function PageBackdrop(): React.JSX.Element {
  return (
    <div className="tb-play__backdrop" aria-hidden="true">
      <svg width="100%" height="100%" aria-hidden="true">
        <defs>
          <filter id="tb-play-grain" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="11" />
            <feColorMatrix values="0 0 0 0 0.16  0 0 0 0 0.12  0 0 0 0 0.08  0 0 0 0.05 0" />
          </filter>
        </defs>
        <rect width="100%" height="100%" filter="url(#tb-play-grain)" />
      </svg>
    </div>
  );
}
